/*
  The generic family a site font falls back to: while the web font is still loading, and for
  characters it lacks. Without one the browser uses its default font, which is Times - a serif
  in place of a sans, much narrower than most text fonts, so the page reflowed once the font
  arrived (and Safari sized some boxes for Times for good, see relayout_opened_answer in
  StoryBlock.svelte).

  The category comes from the font file itself: analyze_font_object.js classifies every uploaded
  face (name, PANOSE, glyph shapes, metrics) and stores `category` and `sub_category`. A special
  category such as display or script keeps the serif/sans reading in `sub_category`, so both are
  checked. Fonts that cannot be placed (script, symbol, …) keep the browser default.
*/

const SERIF_CATEGORIES = new Set(['serif', 'slab_serif', 'old_style', 'transitional', 'modern']);
const SANS_CATEGORIES = new Set(['sans_serif', 'geometric', 'humanist', 'grotesque', 'neo_grotesque']);

/**
 * @param {{ font_faces?: Array<{ category?: string, sub_category?: string, style?: string, weight?: number }> } | undefined} family_spec
 * @returns {'serif' | 'sans-serif' | 'monospace' | null}
 */
export function generic_font_family(family_spec) {
  // The upright regular face describes the family best, then other uprights, then italics. A face
  // that cannot be placed (a subset with a few glyphs, say) leaves the decision to the next one.
  const rank = (/** @type {{ style?: string, weight?: number }} */ face) =>
    (face.style === 'italic' ? 2 : 0) + ((face.weight ?? 400) === 400 ? 0 : 1);
  const faces = [...(family_spec?.font_faces ?? [])].sort((a, b) => rank(a) - rank(b));

  for (const face of faces) {
    for (const category of [face.category, face.sub_category]) {
      if (category === 'monospace') return 'monospace';
      if (SERIF_CATEGORIES.has(category)) return 'serif';
      if (SANS_CATEGORIES.has(category)) return 'sans-serif';
    }
  }
  return null;
}

/**
 * The value for a `font-family` declaration: the site font, then its generic fallback.
 * @param {string} font_family
 * @param {Parameters<typeof generic_font_family>[0]} family_spec
 */
export function font_family_stack(font_family, family_spec) {
  const generic = generic_font_family(family_spec);
  return generic ? `"${font_family}", ${generic}` : `"${font_family}"`;
}
